/**
 * Živý výpočet celkových sumací nákladů schématu v domácí měně.
 *
 * Horní panel i termíny se sdíleným schématem ukazují součty, které se přepočítávají do domácí měny
 * podle fixovaného kurzovního lístku série (viz `buildRateLookup`). Měna, která ještě není v lístku
 * (nově přidaná ve schématu), se do uložení počítá kurzem 1 (odhad) — po uložení server kurz zafixuje.
 *
 * Sčítá se podle typu nákladu:
 *  - `None`    → nezapočítává se,
 *  - `Onsite`  → „na místě" (paxOnsite / grpOnsite),
 *  - jinak     → „kalkulační" (paxCalc / grpCalc).
 * `tot*` přidávají podíl skupinových nákladů na osobu (grp / pax).
 */
import type { FixedExchangeRateDto } from '@/api/generated/schemas';
import { PlaceholderCostType } from '@/api/generated/schemas';

import type { Currency } from './currency';
import type { BoardModel } from './types';

export interface RateLookup {
  /** Kód domácí měny, ve které jsou všechny součty. */
  domesticCurrency: string;
  /** Kurzy z fixovaného lístku série: kód měny → kolik domácí měny je 1 jednotka měny. */
  rates: Map<string, number>;
}

export interface CostTotals {
  paxCalc: number;
  paxOnsite: number;
  grpCalc: number;
  grpOnsite: number;
  /** Náklad na osobu vč. podílu skupinových (paxCalc + grpCalc / pax). */
  totPaxCalc: number;
  totPaxOnsite: number;
  /** Kód domácí měny. */
  currency: string;
}

/** Sestaví přepočtovou tabulku z domácí měny a fixovaného kurzovního lístku série. */
export function buildRateLookup(
  domesticCurrency: string,
  exchangeRates: FixedExchangeRateDto[]
): RateLookup {
  const rates = new Map<string, number>();
  for (const rate of exchangeRates) {
    rates.set(rate.currency, rate.rateToDomestic);
  }
  return { domesticCurrency, rates };
}

/** Přepočte částku v dané měně do domácí měny (neznámá měna → kurz 1). */
export function toDomestic(amount: number, currency: Currency, lookup: RateLookup): number {
  if (currency === lookup.domesticCurrency) return amount;
  const rate = lookup.rates.get(currency);
  return rate != null ? amount * rate : amount;
}

/** Sečte náklady celého schématu do 4 hodnot v domácí měně + odvozené „na osobu". */
export function computeCostTotals(model: BoardModel, lookup: RateLookup, pax: number): CostTotals {
  let paxCalc = 0;
  let paxOnsite = 0;
  let grpCalc = 0;
  let grpOnsite = 0;

  for (const part of model.parts) {
    if (part.costType === PlaceholderCostType.None) continue;
    const px = toDomestic(part.costPax ?? 0, part.currency, lookup);
    const gp = toDomestic(part.costGroup ?? 0, part.currency, lookup);
    if (part.costType === PlaceholderCostType.Onsite) {
      paxOnsite += px;
      grpOnsite += gp;
    } else {
      paxCalc += px;
      grpCalc += gp;
    }
  }

  const safePax = pax > 0 ? pax : 1;
  return {
    paxCalc,
    paxOnsite,
    grpCalc,
    grpOnsite,
    totPaxCalc: paxCalc + grpCalc / safePax,
    totPaxOnsite: paxOnsite + grpOnsite / safePax,
    currency: lookup.domesticCurrency,
  };
}
